import React from "react";
import { useLocation, Route, Routes, Navigate, useNavigate } from "react-router-dom";
import { Container } from "reactstrap";
import AdminNavbar from "components/Navbars/AdminNavbar.js";
import AdminFooter from "components/Footers/AdminFooter.js";
import Sidebar from "components/Sidebar/Sidebar.js";
import { ThemeContext } from "contexts/ThemeContext";
import routes from "routes.js";
import { useKeycloak } from "variables/keycloak";
import useHrmsDeviceStatusPoll from "utils/deviceStatusPoll";
import useHrmsProxyStatusPoll from "utils/proxyStatusPoll";

function Admin() {
  const [sidenavOpen, setSidenavOpen] = React.useState(true);
  const [selectedSite, setSelectedSite] = React.useState(null);
  const keycloak = useKeycloak();
  const location = useLocation();
  const navigate = useNavigate();
  const mainContentRef = React.useRef(null);
  const { theme } = React.useContext(ThemeContext);

  const isDealerRoute = location.pathname.startsWith("/dealer");

  const user = React.useMemo(() => {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  }, []);

  useHrmsDeviceStatusPoll(selectedSite?.site_uuid, {
    enabled: !!user && !!selectedSite?.site_uuid && !isDealerRoute,
  });

  useHrmsProxyStatusPoll(selectedSite?.site_uuid, {
    enabled: !!user && !!selectedSite?.site_uuid && !isDealerRoute,
    intervalMs: 60000, // 60s
  });

  // Scroll to top on route change
  React.useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    if (mainContentRef.current) {
      mainContentRef.current.scrollTop = 0;
    }
  }, [location]);

  // 🔑 Listen for site/context changes and refresh state
  React.useEffect(() => {
    const loadSite = () => {
      const raw = localStorage.getItem("selectedSite");
      setSelectedSite(raw ? JSON.parse(raw) : null);
    };

    loadSite(); // initial load
    window.addEventListener("appContextChanged", loadSite);
    return () => window.removeEventListener("appContextChanged", loadSite);
  }, []);

  React.useEffect(() => {
    if (isDealerRoute) {
      setSelectedSite(null); // always clear it
      setSidenavOpen(false);
      document.body.classList.remove("g-sidenav-pinned");
      document.body.classList.add("g-sidenav-hidden");
    }
  }, [isDealerRoute]);


  // In Admin.js
  React.useEffect(() => {
    const handleSiteChange = () => {
      if (location.pathname.startsWith("/dealer")) {
        setSelectedSite(null);
        setSidenavOpen(false);
        document.body.classList.remove("g-sidenav-pinned");
        document.body.classList.add("g-sidenav-hidden");
      }
    };

    window.addEventListener("siteChanged", handleSiteChange);

    return () => window.removeEventListener("siteChanged", handleSiteChange);
  }, [location]);

  React.useLayoutEffect(() => {
    if (isDealerRoute) {
      document.body.classList.remove("g-sidenav-pinned");
      document.body.classList.add("g-sidenav-hidden");
    }
  }, [location.pathname]);

  const getRoutes = (routes) => {
    let allRoutes = [];
    routes.forEach((prop, key) => {
      if (prop.collapse) {
        allRoutes = allRoutes.concat(getRoutes(prop.views));
      }
      if (prop.layout === "/admin") {
        allRoutes.push(
          <Route path={prop.path} element={prop.component} key={key} />
        );
      }
    });
    return allRoutes;
  };

  const getBrandText = (path) => {
    let name = "Brand";
    routes.forEach((route) => {
      if (route.collapse) {
        route.views.forEach((view) => {
          if (path.indexOf(view.layout + view.path) !== -1) {
            name = view.name;
          }
        });
      } else if (path.indexOf(route.layout + route.path) !== -1) {
        name = route.name;
      }
    });
    return name;
  };

  const toggleSidenav = () => {
    console.log("toggleSidenav called", { sidenavOpen });
    if (document.body.classList.contains("g-sidenav-pinned")) {
      document.body.classList.remove("g-sidenav-pinned");
      document.body.classList.add("g-sidenav-hidden");
    } else {
      document.body.classList.add("g-sidenav-pinned");
      document.body.classList.remove("g-sidenav-hidden");
    }
    setSidenavOpen(!sidenavOpen);
  };

  const handleLogout = async () => {
    localStorage.removeItem('user');
    localStorage.removeItem('selectedSite');

    try {
      keycloak?.logout({
        redirectUri: `${window.location.origin}/auth/login`
      });
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  // Redirect if not logged in
  React.useEffect(() => {
    const user = localStorage.getItem("user");
    if (!user) {
      navigate("/auth/login");
    }
  }, [navigate]);

  console.log("Admin layout render", { selectedSite, sidenavOpen, location: location.pathname });

  return (
    <>
      {!isDealerRoute && selectedSite && (
        <Sidebar
          key={(selectedSite?.site_uuid || "no-site")}
          routes={routes}
          toggleSidenav={toggleSidenav}
          sidenavOpen={sidenavOpen}
          logo={{
            innerLink: "/admin/home",
            imgSrc: theme === "dark"
              ? require("assets/img/brand/husaria-logo-dark.png")
              : require("assets/img/brand/husaria-logo.png"),
            imgAlt: "Husaria Logo",
          }}
        />
      )}

      <div
        key={selectedSite?.site_uuid}
        className="main-content"
        ref={mainContentRef}
      >
        <AdminNavbar
          theme="dark"
          toggleSidenav={toggleSidenav}
          sidenavOpen={sidenavOpen}
          brandText={getBrandText(location.pathname)}
          onLogout={handleLogout}
        />

        <Routes>
          {getRoutes(routes)}
          <Route path="/" element={<Navigate to="/admin/home" replace />} />
          <Route path="*" element={<Navigate to="/admin/home" replace />} />
        </Routes>

        <Container fluid>
          <AdminFooter />
        </Container>
      </div>
    </>
  );
}

export default Admin;
